import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createCategory, updateCategory, fetchCategoryById } from './categorySlice';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import log from '../../logger';
import ReactForm from '../../components/ReactForm';
import { paths } from '../../routes/helpers';
import { getImageUrl, toLocalUploadPath } from '../../utils/urlUtils';
import ImageGallery from '../../components/ImageGallery';

const CategoryCreateEdit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const loading = useSelector((state) => state.category.loading);
  const error = useSelector((state) => state.category.error);
  const category = useSelector((state) => 
    categoryId ? state.category.items.find(b => b._id === categoryId) : null
  );

  const [imageFiles, setImageFiles] = useState([]);
  const [existingImage, setExistingImage] = useState('');

  useEffect(() => {
    if (categoryId) {
      dispatch(fetchCategoryById(categoryId));
    }
  }, [categoryId, dispatch]);

  useEffect(() => {
    if (category && category.image) {
      setExistingImage(getImageUrl(category.image));
    }
  }, [category]);

  const handleImageChange = (event) => {
    const files = Array.from(event.target.files);
    setImageFiles(files);
  };

  const handleRemoveImage = (index) => {
    setImageFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = () => {
    setExistingImage('');
  };

  const handleSubmit = async (formData) => {
    try {
      const processedData = new FormData();
      processedData.append('label', formData.label);
      processedData.append('status', parseInt(formData.status));
      
      if (imageFiles.length > 0) {
        processedData.append('image', imageFiles[0]);
      } else if (existingImage && categoryId) {
        // Keep existing image
        processedData.append('existingImage', toLocalUploadPath(existingImage));
      }

      if (categoryId) {
        await dispatch(updateCategory({ id: categoryId, formData: processedData })).unwrap();
        toast.success('Category updated successfully!');
        log.info('Category updated:', formData.label);
      } else {
        await dispatch(createCategory(processedData)).unwrap();
        toast.success('Category created successfully!');
        log.info('Category created:', formData.label);
      }
      navigate(paths.private.category.list());
    } catch (err) {
      toast.error(err.message || 'An error occurred');
      log.error('Error creating/updating category:', err);
    }
  };

  const fields = [
    {
      name: 'label',
      label: 'Label',
      type: 'text',
      required: true,
      placeholder: 'Enter category label'
    },
    {
      name: 'image',
      label: 'Image',
      type: 'file',
      accept: 'image/*',
      required: !categoryId || (!existingImage && imageFiles.length === 0),
      onChange: handleImageChange,
      preview: (
        <ImageGallery 
          existingImages={imageFiles.length > 0 ? [] : existingImage ? [existingImage] : []}
          imageFiles={imageFiles}
          onRemoveExisting={handleRemoveExistingImage}
          onRemoveNew={handleRemoveImage}
          loading={loading && category?.image && !existingImage}
        />
      )
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      defaultValue: "0",
      options: [
        { value: "0", label: 'Inactive' },
        { value: "1", label: 'Active' },
        { value: "2", label: 'Suspended' }
      ]
    }
  ];

  const defaultValues = category ? {
    label: category.label,
    image: [],
    status: category.status.toString()
  } : {
    label: '',
    image: [],
    status: "1"
  };

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">
        {categoryId ? 'Edit Category' : 'Create Category'}
      </h2>
      <ReactForm
        fields={fields}
        onSubmit={handleSubmit}
        loading={loading}
        error={error}
        defaultValues={defaultValues}
        submitText={categoryId ? 'Update Category' : 'Create Category'}
      />
    </div>
  );
};

export default CategoryCreateEdit; 